// =============================================================================
// PortalCountdownContext — shares the single global countdown handle
// =============================================================================
// PURPOSE: Allows any portal page/component to call countdown.start() without
//          prop-drilling through AppLayout → Routes → Page. The handle instance
//          lives in App.tsx (AppLayout), mounted once, survives route changes.
//
// USAGE:
//   // In a page: import { usePortalCountdown, PORTAL_NOTIFY_WINDOW_MS } from "../context/PortalCountdownContext";
//   const countdown = usePortalCountdown();
//   countdown.start({ taskId, label: "created a task", fireAtMs: Date.now() + PORTAL_NOTIFY_WINDOW_MS });
// =============================================================================

import { createContext, useContext } from "react";
import type { CountdownHandle } from "../hooks/usePortalNotifyCountdown";
import { PORTAL_NOTIFY_WINDOW_SECONDS } from "../hooks/usePortalNotifyCountdown";

// Re-exported so callers get the window from the same import as usePortalCountdown.
export {
  PORTAL_NOTIFY_WINDOW_SECONDS,
  PORTAL_NOTIFY_WINDOW_MS,
} from "../hooks/usePortalNotifyCountdown";

const PortalCountdownContext = createContext<CountdownHandle | null>(null);

export const PortalCountdownProvider = PortalCountdownContext.Provider;

export function usePortalCountdown(): CountdownHandle {
  const ctx = useContext(PortalCountdownContext);
  if (!ctx) {
    // Graceful fallback — return a no-op handle if used outside the provider
    // (e.g. in a test or Storybook). Prevents runtime throws.
    return {
      active: null,
      secondsLeft: PORTAL_NOTIFY_WINDOW_SECONDS,
      previewOpen: false,
      start: () => void 0,
      clear: () => void 0,
      cancel: async () => void 0,
      sendNow: async () => void 0,
      togglePreview: () => void 0,
    };
  }
  return ctx;
}
